import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { graphBuilderApi } from '../../services/api';
import BuildGraphMenu from '../UserHome/BuildGraphMenu';
import CopyToGraphModal from './CopyToGraphModal';
import './ProcessListPage.css';

function ProcessListPage() {
  const { graphId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isGlobalView = !graphId;
  const filterGraphId = graphId || searchParams.get('graph_id') || null;

  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copyToGraphProcess, setCopyToGraphProcess] = useState(null);

  const basePath = graphId
    ? `/user-home/graphs/build/${graphId}/processes`
    : '/user-home/process-maps';

  const fetchProcesses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { status: statusFilter, limit: 1000 };
      if (filterGraphId) params.graph_id = filterGraphId;
      if (searchQuery) params.search = searchQuery;

      const response = await graphBuilderApi.listProcessDefinitions(params);
      setProcesses(response.processes || response || []);
    } catch (err) {
      console.error('Error fetching processes:', err);
      setError(err.message || 'Failed to load processes');
    } finally {
      setLoading(false);
    }
  }, [filterGraphId, searchQuery, statusFilter]);

  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProcesses();
  };

  const handleDelete = async (processId) => {
    try {
      await graphBuilderApi.archiveProcessDefinition(processId);
      setDeleteConfirm(null);
      fetchProcesses();
    } catch (err) {
      console.error('Error deleting process:', err);
      setError(err.message || 'Failed to delete process');
    }
  };

  const handleDuplicate = async (process) => {
    try {
      const fullProcess = await graphBuilderApi.getProcessDefinition(process.process_id);
      const newProcessData = {
        ...fullProcess,
        name: `${fullProcess.name} (Copy)`,
        process_id: undefined,
        created_at: undefined,
      };
      await graphBuilderApi.createProcessDefinition(newProcessData);
      fetchProcesses();
    } catch (err) {
      console.error('Error duplicating process:', err);
      setError(err.message || 'Failed to duplicate process');
    }
  };

  const handleEdit = (processId) => {
    navigate(`${basePath}/${processId}`);
  };

  const handleRun = (processId) => {
    navigate(`${basePath}/${processId}/run`);
  };

  const handleNew = () => {
    navigate(`${basePath}/new`);
  };

  const handleBack = () => {
    if (graphId) {
      navigate(`/user-home/graphs/build/${graphId}`);
    } else {
      navigate('/user-home/graphs');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNodeCount = (process) => {
    if (process.definition?.nodes) {
      return process.definition.nodes.length;
    }
    return process.node_count || 0;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (process, field) => {
    switch (field) {
      case 'name':
        return (process.name || '').toLowerCase();
      case 'nodes':
        return getNodeCount(process);
      case 'status':
        return (process.status || '').toLowerCase();
      case 'created_at':
        return process.created_at ? new Date(process.created_at).getTime() : 0;
      case 'updated_at':
        return process.updated_at ? new Date(process.updated_at).getTime() : 0;
      default:
        return '';
    }
  };

  const sortedProcesses = [...processes].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);
    let comparison = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="process-list-page">
      {graphId && <BuildGraphMenu />}

      <div className="process-list-container">
        <div className="process-list-header">
          <div className="header-left">
            <h1>Process Maps</h1>
            <p className="subtitle">
              {isGlobalView
                ? 'Multi-step workflows across graphs and routers'
                : 'Visual workflow builder for LLM and Platform pipelines'}
            </p>
          </div>
          <div className="header-right">
            <button className="btn-primary" onClick={handleNew}>
              + New Process
            </button>
            {graphId && (
              <button className="btn-secondary" onClick={handleBack}>
                Back to Graph
              </button>
            )}
          </div>
        </div>

        <div className="process-list-toolbar">
          <form className="search-form" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="btn-search">Search</button>
          </form>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <span>Loading processes...</span>
          </div>
        )}

        {!loading && processes.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Process Maps Found</h3>
            <p>Create your first process map to define LLM and Platform workflows</p>
            <button className="btn-primary" onClick={handleNew}>
              Create Process Map
            </button>
          </div>
        )}

        {!loading && processes.length > 0 && (
          <table className="process-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                {isGlobalView && <th>Graph</th>}
                <th className="sortable" onClick={() => handleSort('nodes')}>Nodes{sortIndicator('nodes')}</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                <th className="sortable" onClick={() => handleSort('created_at')}>Created{sortIndicator('created_at')}</th>
                <th className="sortable" onClick={() => handleSort('updated_at')}>Modified{sortIndicator('updated_at')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProcesses.map((process) => (
                <tr key={process.process_id} onClick={() => handleEdit(process.process_id)}>
                  <td className="process-name">
                    <span className="name-text">{process.name}</span>
                    {process.description && (
                      <span className="name-description">
                        {process.description.length > 100
                          ? `${process.description.substring(0, 100)}...`
                          : process.description}
                      </span>
                    )}
                    {process.tags && process.tags.length > 0 && (
                      <div className="process-tags">
                        {process.tags.includes('dynamic_process') && (
                          <span className="tag tag-dynamic-process">Dynamic Process</span>
                        )}
                        {process.tags.filter(t => t !== 'dynamic_process' && t !== 'auto_generated').slice(0, 2).map((tag, idx) => (
                          <span key={idx} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  {isGlobalView && (
                    <td className="process-graph">
                      {process.graph_id ? (
                        <span className="graph-badge">{process.graph_id.substring(0, 8)}...</span>
                      ) : (
                        <span className="global-badge">Global</span>
                      )}
                    </td>
                  )}
                  <td className="process-nodes">{getNodeCount(process)}</td>
                  <td className="process-status">
                    <span className={`status-badge ${process.status}`}>
                      {process.status}
                    </span>
                  </td>
                  <td className="process-created">{formatDate(process.created_at)}</td>
                  <td className="process-created">{formatDate(process.updated_at)}</td>
                  <td className="process-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="action-btn run"
                      onClick={() => handleRun(process.process_id)}
                      title="Run Process"
                    >
                      ▶️
                    </button>
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(process.process_id)}
                      title="Edit Process"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn duplicate"
                      onClick={() => handleDuplicate(process)}
                      title="Duplicate Process"
                    >
                      📋
                    </button>
                    <button
                      className="action-btn copy-to-graph"
                      onClick={() => setCopyToGraphProcess(process)}
                      title="Copy to Another Graph"
                    >
                      🔄
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => setDeleteConfirm(process)}
                      title="Delete Process"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {deleteConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Process</h3>
              <p>Are you sure you want to delete "{deleteConfirm.name}"?</p>
              <p className="warning">This action will archive the process. It can be restored later.</p>
              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn-danger"
                  onClick={() => handleDelete(deleteConfirm.process_id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {copyToGraphProcess && (
          <CopyToGraphModal
            process={copyToGraphProcess}
            currentGraphId={graphId || null}
            onClose={() => setCopyToGraphProcess(null)}
            onSuccess={() => {
              setCopyToGraphProcess(null);
              fetchProcesses();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default ProcessListPage;
